import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../config/supabase';
import { TextField, Button, Box, Typography, Alert, Container, Link, IconButton, InputAdornment, Checkbox, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Snackbar } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { Visibility, VisibilityOff, ArrowForward } from '@mui/icons-material';
import TestimonialRotator from '../common/TestimonialRotator';
import { referralService } from '../../services/referralService';

const StyledForm = styled(motion.div)(({ theme }) => ({
  padding: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  background: 'rgba(255, 255, 255, 0.98)',
  borderRadius: '24px',
  boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
  backdropFilter: 'blur(20px)',
  width: '100%',
  maxWidth: '480px',
  margin: '0 auto',
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(6),
  },
  // Ensure form doesn't get too tall on mobile
  [theme.breakpoints.down('sm')]: {
    borderRadius: '20px',
    margin: theme.spacing(0.5),
    padding: theme.spacing(2),
  },
  // Additional constraint for very short screens
  '@media (max-height: 700px)': {
    padding: theme.spacing(2),
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: '12px',
    '&:hover': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
      },
    },
  },
  // Mobile-specific adjustments
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(1.25),
    '& .MuiInputBase-input': {
      padding: '14px 12px',
    },
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderRadius: '12px',
  fontSize: '1.1rem',
  textTransform: 'none',
  background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
  boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
  '&:hover': {
    background: 'linear-gradient(45deg, #1976D2 30%, #21CBF3 90%)',
  },
}));

const LoginForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  
  // Forgot password states
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  const [resetError, setResetError] = useState('');
  
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check for success message in location state
    if (location.state?.message) {
      setMessage(location.state.message);
    }
    
    // Check URL search params for signup mode and referral code
    const searchParams = new URLSearchParams(location.search);
    const mode = searchParams.get('mode');
    const ref = searchParams.get('ref');
    
    if (mode === 'signup') {
      setIsSignUp(true);
    }
    
    // If there's a referral code, set it and switch to signup mode
    if (ref) {
      setReferralCode(ref.toUpperCase());
      setIsSignUp(true);
      setMessage(`Welcome! You're joining with referral code: ${ref.toUpperCase()}`);
    }
  }, [location]);

  // Add Crisp chatbox
  useEffect(() => {
    // Check if Crisp is already loaded to prevent conflicts
    if (window.$crisp && document.querySelector('script[src="https://client.crisp.chat/l.js"]')) {
      return;
    }

    window.$crisp = [];
    window.CRISP_WEBSITE_ID = "d457b4c6-e683-4db4-836a-0e3aebc840d9";
    
    const script = document.createElement("script");
    script.src = "https://client.crisp.chat/l.js";
    script.async = true;
    document.head.appendChild(script);

    return () => {
      // More thorough cleanup on unmount
      const existingScript = document.querySelector('script[src="https://client.crisp.chat/l.js"]');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
      
      // Clean up Crisp variables
      delete window.$crisp;
      delete window.CRISP_WEBSITE_ID;
      
      // Remove any Crisp-related DOM elements
      const crispElements = document.querySelectorAll('[id*="crisp"], [class*="crisp"]');
      crispElements.forEach(element => {
        element.remove();
      });
    };
  }, []);

  const checkUserQuizStatus = async (userId, retryCount = 0) => {
    try {
      console.log('LoginForm: Checking user status for:', userId, retryCount > 0 ? `(retry ${retryCount})` : '');
      console.log('LoginForm: Supabase client state:', !!supabase, 'URL:', process.env.REACT_APP_SUPABASE_URL?.substring(0, 20) + '...');
      
      // FAST CHECK: First just check if user has active subscription - bypass complex logic
      console.log('LoginForm: Quick subscription check...');
      const quickStartTime = Date.now();
      
      try {
        const { data: quickProfile, error: quickError } = await supabase
          .from('user_profiles')
          .select('subscription_status, quiz_completed, token_balance, email, username, avatar_url, xp, level, id, voice_preferences, welness_goals')
          .eq('id', userId)
          .maybeSingle();
          
        const quickDuration = Date.now() - quickStartTime;
        console.log('LoginForm: Quick check completed in', quickDuration + 'ms', 'Found:', !!quickProfile);
        
        if (!quickError && quickProfile && quickProfile.subscription_status === 'active') {
          console.log('LoginForm: Active subscription found! Sending directly to conversation');
          return {
            hasCompletedQuiz: true,
            hasActiveSubscription: true,
            subscriptionStatus: 'active',
            profile: quickProfile,
            profileError: null
          };
        }
      } catch (quickErr) {
        console.log('LoginForm: Quick check failed, falling back to full check:', quickErr.message);
      }
      
      // FULL CHECK: If quick check failed or user doesn't have active subscription
      console.log('LoginForm: Executing full database query...');
      const timeoutDuration = 5000; // 5 second timeout
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Quiz status check timeout')), timeoutDuration);
      });
      
      const queryStartTime = Date.now();
      
      // Full query for complete profile data
      const queryPromise = supabase
        .from('user_profiles')
        .select('quiz_completed, subscription_status, subscription_ends_at, email, token_balance, username, avatar_url, xp, level, id, voice_preferences, welness_goals')
        .eq('id', userId)
        .maybeSingle();
      
      const { data: profile, error: profileError } = await Promise.race([queryPromise, timeoutPromise]);
      
      const queryDuration = Date.now() - queryStartTime;
      console.log('LoginForm: Full query completed in', queryDuration + 'ms', 'Profile found:', !!profile, 'Error:', !!profileError);
      
      if (profileError) {
        console.log('LoginForm: Profile error details:', profileError);
        if (profileError.code === 'PGRST116') {
          console.log('LoginForm: No user profile found (PGRST116), user needs to complete quiz');
          return {
            hasCompletedQuiz: false,
            hasActiveSubscription: false,
            subscriptionStatus: undefined,
            profile: null,
            profileError: null
          };
        } else {
          console.error('LoginForm: Database error during quiz status check:', profileError);
          return {
            hasCompletedQuiz: false,
            hasActiveSubscription: false,
            subscriptionStatus: undefined,
            profile: null,
            profileError: 'database_error'
          };
        }
      }

      if (!profile) {
        console.log('LoginForm: No profile found, user needs to complete quiz');
        return {
          hasCompletedQuiz: false,
          hasActiveSubscription: false,
          subscriptionStatus: undefined,
          profile: null,
          profileError: null
        };
      }

      const quiz_completed = profile.quiz_completed === true;
      const subscription_status = profile.subscription_status;
      const isActiveSubscription = subscription_status === 'active';
      
      console.log('LoginForm: Full quiz status check result:', {
        hasProfile: !!profile,
        quiz_completed,
        subscription_status,
        isActiveSubscription
      });

      return {
        hasCompletedQuiz: quiz_completed,
        hasActiveSubscription: isActiveSubscription,
        subscriptionStatus: subscription_status,
        profile: profile || null,
        profileError: null
      };
    } catch (error) {
      console.error('Error checking user status:', error);
      
      if (!navigator.onLine) {
        console.log('LoginForm: Network offline, cannot check quiz status');
        return { 
          hasCompletedQuiz: false, 
          hasActiveSubscription: false,
          subscriptionStatus: undefined,
          profile: null, 
          profileError: 'offline'
        };
      }
      
      if (error.message.includes('fetch') || error.message.includes('network') || error.message.includes('Failed to fetch')) {
        console.log('LoginForm: Network/connection error during quiz status check');
        return { 
          hasCompletedQuiz: false, 
          hasActiveSubscription: false,
          subscriptionStatus: undefined,
          profile: null, 
          profileError: 'network_error'
        };
      }
      
      if (error.message.includes('timeout') && retryCount === 0) {
        console.log('LoginForm: Retrying quiz status check once...');
        await new Promise(resolve => setTimeout(resolve, 200));
        return await checkUserQuizStatus(userId, 1);
      }
      
      if (error.message.includes('timeout')) {
        console.log('LoginForm: Quiz status check timed out after retry. Using intelligent fallback...');
        console.log('LoginForm: Timeout occurred, defaulting to quiz navigation');
        return { 
          hasCompletedQuiz: false, 
          hasActiveSubscription: false,
          subscriptionStatus: undefined,
          profile: null, 
          profileError: 'timeout'
        };
      }
      
      console.log('LoginForm: Quiz status check failed with error, defaulting to quiz:', error.message);
      return { 
        hasCompletedQuiz: false, 
        hasActiveSubscription: false,
        subscriptionStatus: undefined,
        profile: null, 
        profileError: error.message 
      };
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('LoginForm: handleSubmit called', { email, isSignUp });
    
    if (loading) return; // Prevent multiple submissions
    
    // Simple client-side rate limiting for signup attempts
    if (isSignUp) {
      // Check if this email has hit rate limits recently
      const emailRateLimit = localStorage.getItem(`emailRateLimit_${email}`);
      if (emailRateLimit) {
        const timeSinceRateLimit = Date.now() - parseInt(emailRateLimit);
        if (timeSinceRateLimit < 900000) { // 15 minutes
          const remainingMinutes = Math.ceil((900000 - timeSinceRateLimit) / 60000);
          setError(`🚫 This email hit rate limits recently. Please wait ${remainingMinutes} more minutes or try a different email address.`);
          setMessage('💡 You can also try signing in if you already have an account.');
          return;
        } else {
          // Rate limit expired, remove it
          localStorage.removeItem(`emailRateLimit_${email}`);
        }
      }
      
      // Check for general signup attempts
      const lastSignupAttempt = localStorage.getItem('lastSignupAttempt');
      if (lastSignupAttempt) {
        const timeSinceLastAttempt = Date.now() - parseInt(lastSignupAttempt);
        if (timeSinceLastAttempt < 30000) { // 30 seconds between signup attempts
          const remainingSeconds = Math.ceil((30000 - timeSinceLastAttempt) / 1000);
          setError(`Please wait ${remainingSeconds} seconds before trying to sign up again.`);
          return;
        }
      }
      localStorage.setItem('lastSignupAttempt', Date.now().toString());
    }
    
    setError(null);
    setLoading(true);
    console.log('LoginForm: Loading state set to true');

    // Set a timeout to reset loading state in case of hanging
    const loadingTimeout = setTimeout(() => {
      console.log('LoginForm: Timeout reached, resetting loading state');
      setLoading(false);
      setError('Authentication timeout. Please try again.');
    }, 10000); // 10 second timeout

    try {
      let result;
      
      if (isSignUp) {
        console.log('LoginForm: Attempting signup');
        // Sign up new user
        result = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              email: email
            }
          }
        });
        
        // Check if user already exists
        if (result.error && result.error.message.includes('User already registered')) {
          console.log('LoginForm: User already exists');
          setError('An account with this email already exists. Please sign in instead.');
          setMessage('Account already exists! Switching to sign in in 3 seconds...');
          
          // Wait a moment then switch to sign in mode
          setTimeout(() => {
            setIsSignUp(false);
            setError(null);
            setMessage('Switched to sign in mode. Please enter your password.');
            setPassword(''); // Clear password for security
            setLoading(false);
          }, 3000);
          
          return;
        }
      } else {
        console.log('LoginForm: Attempting signin');
        // Sign in existing user
        result = await supabase.auth.signInWithPassword({
          email,
          password
        });
      }

      console.log('LoginForm: Auth result received', { success: !!result.data?.user, error: !!result.error });
      const { data, error: authError } = result;

      // Clear the timeout since we got a response
      clearTimeout(loadingTimeout);

      if (authError) {
        console.log('LoginForm: Auth error:', authError.message);
        
        // Handle specific error types with user-friendly messages
        let userFriendlyError = authError.message;
        
        if (authError.message.includes('email rate limit exceeded') || 
            authError.message.includes('rate limit exceeded') || 
            authError.message.includes('429')) {
          
          if (isSignUp) {
            userFriendlyError = '⏰ Too many signup attempts detected. Please wait 10-15 minutes before trying again, or try signing in if you already have an account.';
            setMessage('💡 If you already created an account, try switching to "Sign In" instead!');
            
            // Store rate limit for this email to prevent further attempts
            localStorage.setItem(`emailRateLimit_${email}`, Date.now().toString());
            
            // Suggest switching to sign in mode after a delay
            setTimeout(() => {
              setIsSignUp(false);
              setMessage('Switched to sign in mode. If you have an account, try signing in instead.');
            }, 3000);
          } else {
            userFriendlyError = '⏰ Too many login attempts. Please wait 10-15 minutes before trying again.';
            setMessage('🔄 You can also try refreshing the page and using a different email address.');
          }
          
        } else if (authError.message.includes('Invalid login credentials')) {
          userFriendlyError = '❌ Invalid email or password. Please check your credentials and try again.';
        } else if (authError.message.includes('Email not confirmed')) {
          userFriendlyError = '📧 Please check your email and click the verification link before signing in.';
        } else if (authError.message.includes('User already registered')) {
          userFriendlyError = '✅ Account already exists! Switching to sign in mode...';
          setMessage('Account already exists! Switching to sign in in 3 seconds...');
          setTimeout(() => {
            setIsSignUp(false);
            setError(null);
            setMessage('Switched to sign in mode. Please enter your password.');
            setPassword('');
            setLoading(false);
          }, 3000);
          return;
        }
        
        setError(userFriendlyError);
        setLoading(false);
        return;
      }

      if (data?.user) {
        console.log('LoginForm: User authenticated successfully');
        // Clear the timeout since authentication succeeded
        clearTimeout(loadingTimeout);
        
        if (isSignUp) {
          // Apply referral code if present
          if (referralCode) {
            try {
              console.log('LoginForm: Applying referral code for new user:', referralCode);
              await referralService.applyReferralCode(referralCode, data.user.id);
              console.log('LoginForm: Referral code applied successfully');
            } catch (referralError) {
              console.warn('LoginForm: Failed to apply referral code:', referralError);
              // Don't block signup flow for referral errors
            }
          }
          
          // For new users, check if email is confirmed
          if (!data.user.email_confirmed_at) {
            console.log('LoginForm: New user signup, email verification required');
            // Store email for verification page
            localStorage.setItem('pendingVerificationEmail', email);
            setMessage('Success! Please check your email to verify your account, then return here to sign in.');
            // Switch to sign-in mode after showing success message
            setTimeout(() => {
              setIsSignUp(false);
              setPassword('');
              setLoading(false);
            }, 2000);
          } else {
            // Email already confirmed, go to quiz
            console.log('LoginForm: New user signup with confirmed email, navigating to quiz');
            setLoading(false);
            // Small delay to ensure auth state is fully updated
            setTimeout(() => navigate('/quiz'), 100);
          }
        } else {
          // For existing users, check their quiz and subscription status
          console.log('LoginForm: Existing user login, checking status...');
          
          // Add small delay to ensure auth state is fully propagated to Supabase
          await new Promise(resolve => setTimeout(resolve, 100));
          
          try {
            const { hasCompletedQuiz, hasActiveSubscription, subscriptionStatus, profile, profileError } = await checkUserQuizStatus(data.user.id);

            console.log('LoginForm: Routing decision:', {
              hasCompletedQuiz,
              hasActiveSubscription,
              subscriptionStatus,
              hasProfile: !!profile,
              userCreatedAt: data.user.created_at,
              profileError: profileError
            });

            // Handle different types of errors with appropriate user messages
            if (profileError === 'timeout') {
              console.log('LoginForm: Profile check timed out, directing to quiz with explanation');
              setMessage('⏱️ Connection slow - taking you to complete your profile setup...');
              setLoading(false);
              setTimeout(() => navigate('/quiz'), 1500); // Give time to read message
              return;
            }
            
            if (profileError === 'offline') {
              console.log('LoginForm: User offline, directing to quiz with explanation');
              setMessage('📶 You appear to be offline. Taking you to setup when connection improves...');
              setLoading(false);
              setTimeout(() => navigate('/quiz'), 2000);
              return;
            }
            
            if (profileError === 'network_error') {
              console.log('LoginForm: Network error, directing to quiz with explanation');
              setMessage('🌐 Connection issue detected - completing setup locally...');
              setLoading(false);
              setTimeout(() => navigate('/quiz'), 1500);
              return;
            }

            // CRITICAL: If no profile exists or error occurred, user must complete quiz first
            if (!profile || profileError) {
              const errorMessage = profileError && profileError !== 'timeout' ? 
                'Profile check failed - completing setup...' : 
                'Setting up your profile...';
              console.log('LoginForm: No profile found or error occurred, navigating to quiz to create profile');
              setMessage(`🔧 ${errorMessage}`);
              setLoading(false);
              setTimeout(() => navigate('/quiz'), 1000);
              return;
            }

            // Implement the routing logic based on quiz_completed and subscription_status
            if (!hasCompletedQuiz) {
              // If quiz_completed is false -> send to quiz
              console.log('LoginForm: Quiz not completed, navigating to quiz');
              setMessage('📋 Completing your personalized wellness assessment...');
              setLoading(false);
              setTimeout(() => navigate('/quiz'), 800);
            } else if (hasCompletedQuiz && hasActiveSubscription) {
              // If quiz_completed is true AND subscription_status is active -> send to conversation
              console.log('LoginForm: Quiz completed and active subscription, navigating to conversation');
              setMessage('✅ Welcome back! Loading your wellness companion...');
              setLoading(false);
              setTimeout(() => navigate('/conversation'), 800);
            } else if (hasCompletedQuiz && !hasActiveSubscription) {
              // If quiz_completed is true BUT subscription_status is not active -> send to subscribe
              console.log('LoginForm: Quiz completed but no active subscription, navigating to subscribe. Status:', subscriptionStatus);
              setMessage('💳 Setting up your subscription...');
              setLoading(false);
              setTimeout(() => navigate('/subscribe'), 800);
            } else {
              // Fallback case - shouldn't happen but handle gracefully
              console.log('LoginForm: Unexpected state, defaulting to quiz navigation');
              setMessage('🔧 Setting up your account...');
              setLoading(false);
              setTimeout(() => navigate('/quiz'), 1000);
            }
          } catch (statusError) {
            console.error('LoginForm: Error during status check:', statusError);
            setMessage('🔧 Setting up your account...');
            setLoading(false);
            // If status check fails, default to quiz to be safe
            console.log('LoginForm: Status check failed, defaulting to quiz navigation');
            setTimeout(() => navigate('/quiz'), 1000);
          }
        }
      } else {
        console.log('LoginForm: No user data in result');
        clearTimeout(loadingTimeout);
        setLoading(false);
        setError('Authentication succeeded but no user data received. Please try again.');
      }
    } catch (error) {
      console.error('LoginForm: Authentication error:', error);
      clearTimeout(loadingTimeout);
      setError(error.message);
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Forgot password handler
  const handleForgotPassword = async () => {
    if (!resetEmail) {
      setResetError('Please enter your email address');
      return;
    }

    setResetLoading(true);
    setResetError('');
    setResetMessage('');

    try {
      // Clear any existing session issues first
      await supabase.auth.getSession();
      
      // Create the redirect URL pointing to auth callback for proper processing
      const redirectUrl = `${window.location.protocol}//${window.location.host}/auth/callback`;
      console.log('Sending password reset to:', resetEmail, 'with redirect:', redirectUrl);
      
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: redirectUrl,
      });

      if (error) {
        console.error('Password reset error:', error);
        
        // Handle specific error types
        let userFriendlyError = error.message;
        
        if (error.message.includes('AuthSessionMissingError') || 
            error.message.includes('session missing')) {
          userFriendlyError = 'Session error occurred. Please refresh the page and try again.';
        } else if (error.message.includes('rate limit') || 
                   error.message.includes('429') ||
                   error.message.includes('email rate limit exceeded')) {
          userFriendlyError = 'Too many password reset attempts. Please wait 10-15 minutes before trying again.';
        } else if (error.message.includes('not found') || 
                   error.message.includes('User not found')) {
          userFriendlyError = 'No account found with this email address. Please check the email or create a new account.';
        } else if (error.message.includes('invalid email')) {
          userFriendlyError = 'Please enter a valid email address.';
        }
        
        setResetError(userFriendlyError);
      } else {
        setResetMessage('Password reset email sent! 📨 Check your inbox immediately and click the link right away - it expires quickly for security.');
        setTimeout(() => {
          setForgotPasswordOpen(false);
          setResetEmail('');
          setResetError('');
        }, 4000); // Give more time to read the important message
      }
    } catch (error) {
      console.error('Password reset error:', error);
      
      // Handle different types of errors
      if (error.message.includes('AuthSessionMissingError') || 
          error.message.includes('session missing')) {
        setResetError('Session error. Please refresh the page and try again.');
      } else if (error.message.includes('network') || 
                 error.message.includes('fetch')) {
        setResetError('Network error. Please check your connection and try again.');
      } else if (error.message.includes('rate limit') || 
                 error.message.includes('429') ||
                 error.message.includes('email rate limit exceeded')) {
        setResetError('Too many password reset attempts. Please wait 10-15 minutes before trying again.');
      } else {
        setResetError('An unexpected error occurred. Please try again or contact support.');
      }
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0A2647 0%, #144272 50%, #205295 100%)',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        // Ensure proper scrolling on mobile and prevent top cutoff
        overflow: 'auto',
        paddingTop: { xs: 2, sm: 3, md: 4 },
        paddingBottom: { xs: 2, sm: 3, md: 4 },
        '@media (max-height: 800px)': {
          paddingTop: 2,
          paddingBottom: 2,
        },
      }}
    >
      {/* Decorative background elements */}
      <Box
        sx={{
          position: 'absolute',
          width: '500px',
          height: '500px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(33, 150, 243, 0.1) 0%, rgba(33, 150, 243, 0) 70%)',
          top: '-200px',
          right: '-200px',
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          width: '600px',
          height: '600px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(3, 169, 244, 0.1) 0%, rgba(3, 169, 244, 0) 70%)',
          bottom: '-300px',
          left: '-200px',
          zIndex: 0,
        }}
      />

      <Container 
        maxWidth="sm" 
        sx={{ 
          position: 'relative', 
          zIndex: 1,
          flex: 1,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          py: { xs: 2, sm: 4, md: 6 },
          px: { xs: 1, sm: 2 },
          // Ensure adequate spacing but allow natural scrolling
          minHeight: 'calc(100vh - 32px)',
          // For taller screens, we can add more top padding to center visually
          '@media (min-height: 800px)': {
            paddingTop: { xs: 4, sm: 6, md: 8 },
          },
          '@media (min-height: 1000px)': {
            paddingTop: { xs: 6, sm: 8, md: 12 },
          },
        }}
      >
        <StyledForm
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Brand */}
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.3 }}
            style={{ textAlign: 'center' }}
          >
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontWeight: 700,
                color: '#0A2647',
                mb: { xs: 0.5, sm: 1 },
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }
              }}
            >
              Sanity Reps
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                color: 'text.secondary',
                mb: { xs: 1, sm: 2 },
                fontSize: { xs: '0.85rem', sm: '0.9rem', md: '1rem' }
              }}
            >
              Your Personal AI Wellness Assistant
            </Typography>
            
            {/* Try it for free banner - More compact on mobile */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              <Box
                sx={{
                  background: 'linear-gradient(45deg, #ff6b6b, #ffa726, #66bb6a, #42a5f5)',
                  backgroundSize: '300% 300%',
                  animation: 'gradient 3s ease infinite',
                  '@keyframes gradient': {
                    '0%': { backgroundPosition: '0% 50%' },
                    '50%': { backgroundPosition: '100% 50%' },
                    '100%': { backgroundPosition: '0% 50%' },
                  },
                  borderRadius: '20px',
                  padding: { xs: '8px 16px', sm: '12px 24px' },
                  mb: { xs: 1.5, sm: 2, md: 3 },
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
                    animation: 'shine 2s infinite',
                  },
                  '@keyframes shine': {
                    '0%': { left: '-100%' },
                    '100%': { left: '100%' },
                  },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: 'white',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    fontSize: { xs: '0.9rem', sm: '1rem', md: '1.2rem' },
                    textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  🚀 Get Your Free Personal Plan Now! 
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.95)',
                    textAlign: 'center',
                    fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.9rem' },
                    fontWeight: 500,
                    position: 'relative',
                    zIndex: 1,
                    display: { xs: 'none', sm: 'block' },
                    '@media (max-height: 600px)': {
                      display: 'none',
                    },
                  }}
                >
                  Get your personalized breakthrough plan & start healing today!
                </Typography>
              </Box>
            </motion.div>
          </motion.div>

          {/* Testimonial Rotator - Hide on small screens and short viewports */}
          <Box sx={{ 
            width: '100%', 
            mb: { xs: 1, sm: 2, md: 3 },
            display: { xs: 'none', sm: 'block' },
            '@media (max-height: 700px)': {
              display: 'none',
            },
          }}>
            <TestimonialRotator />
          </Box>

          {/* Mobile compact testimonial - Only show on mobile with sufficient height */}
          <Box sx={{ 
            width: '100%', 
            mb: 1.5,
            display: { xs: 'block', sm: 'none' },
            textAlign: 'center',
            px: 1,
            '@media (max-height: 600px)': {
              display: 'none',
            },
          }}>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.85rem',
                fontStyle: 'italic',
                color: 'text.secondary',
                mb: 0.5,
                lineHeight: 1.4,
              }}
            >
              "This app has been a game-changer for my mental health."
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.primary',
                fontWeight: 500,
                fontSize: '0.75rem'
              }}
            >
              — Sarah M.
            </Typography>
          </Box>

          {/* Alerts */}
          {error && (
            <Alert 
              severity="error" 
              sx={{ width: '100%', mb: { xs: 1.5, sm: 2 }, borderRadius: '12px' }}
            >
              {error}
            </Alert>
          )}

          {message && (
            <Alert 
              severity="success" 
              sx={{ width: '100%', mb: { xs: 1.5, sm: 2 }, borderRadius: '12px' }}
            >
              {message}
            </Alert>
          )}

          {/* Auth Toggle */}
          <Typography 
            variant="h5" 
            sx={{ 
              color: '#0A2647', 
              mb: { xs: 2, sm: 3 }, 
              fontWeight: 600,
              textAlign: 'center',
              fontSize: { xs: '1.3rem', sm: '1.5rem' }
            }}
          >
            {isSignUp ? 'Create Account' : 'Welcome Back'}
          </Typography>

          {/* Form */}
          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <StyledTextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
            
            <StyledTextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete={isSignUp ? 'new-password' : 'current-password'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={togglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Terms and Conditions checkbox for signup */}
            {isSignUp && (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                mt: 2, 
                mb: 2,
                px: 1
              }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                      color="primary"
                      sx={{
                        '& .MuiSvgIcon-root': {
                          fontSize: 20,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: { xs: '0.85rem', sm: '0.9rem' },
                        lineHeight: 1.4,
                        color: 'text.secondary',
                        textAlign: 'center',
                        maxWidth: '280px'
                      }}
                    >
                      I agree to the{' '}
                      <Link
                        href="/terms-and-conditions"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: '#2196F3',
                          textDecoration: 'none',
                          fontWeight: 600,
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            textDecoration: 'underline',
                            color: '#1976D2',
                          },
                        }}
                      >
                        Terms and Conditions
                      </Link>
                    </Typography>
                  }
                  sx={{ 
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    flexDirection: 'row',
                    m: 0,
                    '& .MuiFormControlLabel-label': {
                      ml: 1,
                      pt: '1px'
                    },
                    '& .MuiCheckbox-root': {
                      pt: 0,
                      pb: 0,
                    }
                  }}
                />
              </Box>
            )}

            {/* Rate limit warning for specific email */}
            {isSignUp && email.trim() && (() => {
              const emailRateLimit = localStorage.getItem(`emailRateLimit_${email.trim()}`);
              if (emailRateLimit) {
                const timeSinceRateLimit = Date.now() - parseInt(emailRateLimit);
                if (timeSinceRateLimit < 900000) { // 15 minutes
                  const remainingMinutes = Math.ceil((900000 - timeSinceRateLimit) / 60000);
                  return (
                    <Alert severity="warning" sx={{ mb: 2, borderRadius: '12px' }}>
                      ⏰ This email address hit rate limits recently. Please wait {remainingMinutes} more minute{remainingMinutes !== 1 ? 's' : ''} or try a different email.
                    </Alert>
                  );
                }
              }
              return null;
            })()}

            <StyledButton
              fullWidth
              type="submit"
              disabled={
                loading || 
                !email.trim() || 
                !password.trim() || 
                (isSignUp && !acceptedTerms) ||
                (isSignUp && (() => {
                  // Check if this email is rate limited
                  const emailRateLimit = localStorage.getItem(`emailRateLimit_${email.trim()}`);
                  if (emailRateLimit) {
                    const timeSinceRateLimit = Date.now() - parseInt(emailRateLimit);
                    return timeSinceRateLimit < 900000; // 15 minutes
                  }
                  return false;
                })())
              }
              sx={{ 
                mt: { xs: 1.5, sm: 2 }, 
                mb: { xs: 1.5, sm: 2 },
                ...(isSignUp && {
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
                  color: '#ffffff',
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  letterSpacing: '0.5px',
                  textTransform: 'none',
                  padding: '14px 24px',
                  borderRadius: '16px',
                  border: 'none',
                  position: 'relative',
                  overflow: 'hidden',
                  boxShadow: '0 12px 35px rgba(102, 126, 234, 0.4), 0 0 0 1px rgba(255,255,255,0.1) inset',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0.1) 100%)',
                    zIndex: 1
                  },
                  '& .MuiButton-startIcon, & .MuiButton-endIcon': {
                    position: 'relative',
                    zIndex: 2
                  },
                  '& .MuiButton-endIcon': {
                    marginLeft: '12px',
                    transition: 'transform 0.3s ease'
                  },
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 50%, #e081f9 100%)',
                    boxShadow: '0 20px 50px rgba(102, 126, 234, 0.6), 0 0 0 1px rgba(255,255,255,0.2) inset',
                    transform: 'translateY(-3px) scale(1.02)',
                    '& .MuiButton-endIcon': {
                      transform: 'translateX(4px)'
                    }
                  },
                  '&:active': {
                    transform: 'translateY(-1px) scale(1.01)'
                  },
                  '&:disabled': {
                    background: 'rgba(102, 126, 234, 0.3)',
                    color: 'rgba(255, 255, 255, 0.5)',
                    boxShadow: 'none',
                    transform: 'none'
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                })
              }}
              onClick={(e) => {
                console.log('LoginForm: Button clicked', { 
                  email: email.trim(), 
                  password: password.trim() ? 'has_password' : 'no_password',
                  loading,
                  disabled: loading || !email.trim() || !password.trim()
                });
              }}
              endIcon={isSignUp && !loading ? <ArrowForward sx={{ fontSize: '1.2rem' }} /> : null}
            >
              <span style={{ position: 'relative', zIndex: 2 }}>
                {loading ? 'Processing...' : isSignUp ? 'Start Your Journey' : 'Sign In'}
              </span>
            </StyledButton>

            {/* Toggle between login and signup */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {isSignUp ? 'Already have an account?' : "Don't have an account?"}
                {' '}
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => {
                    setIsSignUp(!isSignUp);
                    setError(null);
                    setMessage('');
                    setAcceptedTerms(false);
                  }}
                  sx={{
                    color: '#2196F3',
                    textDecoration: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {isSignUp ? 'Sign In' : 'Sign Up'}
                </Link>
              </Typography>
              
              {/* Forgot Password Link - Only show on login mode */}
              {!isSignUp && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() => {
                      setResetEmail(email); // Pre-fill with current email
                      setForgotPasswordOpen(true);
                    }}
                    sx={{
                      color: '#2196F3',
                      textDecoration: 'none',
                      fontWeight: 500,
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Forgot your password?
                  </Link>
                </Typography>
              )}
            </Box>

            {/* Features - Hide on mobile and short screens to save space */}
            <Box sx={{ 
              mt: { xs: 1, sm: 2, md: 3 },
              display: { xs: 'none', sm: 'block' },
              '@media (max-height: 700px)': {
                display: 'none',
              },
            }}>
              {['✨ Free wellness assessment', '🔒 Private and secure', '🎯 Personalized guidance'].map((feature, index) => (
                <Typography
                  key={index}
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    mb: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: { xs: '0.8rem', sm: '0.85rem' }
                  }}
                >
                  {feature}
                </Typography>
              ))}
            </Box>

            {/* Forgot Password Dialog */}
            <Dialog
              open={forgotPasswordOpen}
              onClose={() => setForgotPasswordOpen(false)}
              aria-labelledby="forgot-password-dialog-title"
              aria-describedby="forgot-password-dialog-description"
              maxWidth="sm"
              fullWidth
              disablePortal={false}
              hideBackdrop={false}
            >
              <DialogTitle id="forgot-password-dialog-title" sx={{ textAlign: 'center', fontWeight: 600, color: '#0A2647' }}>
                Reset Password
              </DialogTitle>
              <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ mb: 2, textAlign: 'center', color: 'text.secondary' }}>
                  Enter your email address below and we'll send you a link to reset your password.
                </Typography>
                <Alert severity="info" sx={{ mb: 2, borderRadius: '12px' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    📨 Important: Click the reset link immediately after receiving the email. 
                    Reset links expire quickly for security reasons (usually within 5-10 minutes).
                  </Typography>
                </Alert>
                <StyledTextField
                  label="Email"
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  autoComplete="email"
                  sx={{ width: '100%', mb: 2 }}
                />
                
                {/* Error and success messages for password reset */}
                {resetError && (
                  <Alert severity="error" sx={{ width: '100%', mb: 1, borderRadius: '12px' }}>
                    {resetError}
                  </Alert>
                )}

                {resetMessage && (
                  <Alert severity="success" sx={{ width: '100%', mb: 1, borderRadius: '12px' }}>
                    {resetMessage}
                  </Alert>
                )}

                <Button
                  onClick={handleForgotPassword}
                  disabled={resetLoading || !resetEmail}
                  sx={{ 
                    width: '100%', 
                    borderRadius: '12px', 
                    padding: '12px',
                    background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                    color: 'white',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #1976D2 30%, #21CBF3 90%)',
                    },
                    '&:disabled': {
                      background: '#ccc',
                    },
                  }}
                >
                  {resetLoading ? <CircularProgress size={24} color="inherit" /> : 'Send Reset Link'}
                </Button>
              </DialogContent>
              
              <DialogActions sx={{ p: 3, pt: 0 }}>
                <Button
                  onClick={() => {
                    setForgotPasswordOpen(false);
                    setResetEmail('');
                    setResetError('');
                  }}
                  sx={{ color: '#666' }}
                >
                  Cancel
                </Button>
              </DialogActions>
            </Dialog>

            {/* Success Message Snackbar */}
            <Snackbar
              open={!!resetMessage}
              autoHideDuration={6000}
              onClose={() => setResetMessage('')}
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
              <Alert onClose={() => setResetMessage('')} severity="success" sx={{ width: '100%' }}>
                {resetMessage}
              </Alert>
            </Snackbar>
          </Box>
        </StyledForm>
      </Container>
    </Box>
  );
};

export default LoginForm;
